<template>
  <div class="b-modal" :class="{'fullscreen' : isFullScreen}">
    <modal
      v-bind="modalConfig"
      v-on="$listeners"
      @closed="modalClosed"
      @opened="modalOpened"
      @before-open="beforeModalOpen"
      @before-close="beforeModalClose"
      :class="{ 'has-detached-content': isHasDetachedContent }"
    >
      <div v-on="hasScrollListener ? {'scroll' : emitScroll} : {}"
           ref="modal"
           class="modal custom-scrollbar "
           :class="classList"
           tabindex="0">
        <div class="modal-heading">
          <button @click="$modal.hide(name)" class="modal__close" :aria-label="$t('modal_close')"></button>
          <slot name="modal-heading">
            <h2
              v-if="isNeedToShowTitle"
              class="modal__title"
              :class="{ 'modal__title--sm': modalConfig.width < 600 }"
              v-html="title"
            ></h2>
          </slot>
        </div>
        <div class="modal-body custom-scrollbar">
          <slot></slot>
        </div>
      </div>

      <div class="modal modal--detached custom-scrollbar" v-if="isHasDetachedContent">
        <div class="modal-body">
          <slot name="detached-content"></slot>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { debounce } from '@/helpers/debounce'

const defaultModalConfig = {
  pivotX: 0,
  pivotY: 0,
  scrollable: false,
  adaptive: true,
  height: 'auto'
}

export default {
  name: 'ModalProvider',
  data () {
    return {
      isFullScreen: false
    }
  },
  props: {
    title: {
      type: String
    },
    name: {
      type: String,
      required: true
    },
    classList: {
      type: String
    },
    fullscreenBreakpoint: {
      type: Number,
      required: false,
      default: null
    }
  },
  computed: {
    isNeedToShowTitle () {
      return this.title
    },
    modalConfig () {
      return {
        ...defaultModalConfig,
        ...this.$attrs,
        name: this.name
      }
    },
    isHasDetachedContent () {
      return !!this.$slots['detached-content']
    },
    hasScrollListener () {
      return !!this.$listeners?.scroll
    }
  },
  methods: {
    setModalFullscreen: debounce(function () {
      this.isFullScreen = +window.innerWidth <= this.fullscreenBreakpoint
    }, 10),
    modalOpened (evt) {
      const modals = JSON.parse(localStorage.getItem('modalsDisplayTimestamp')) || {}
      localStorage.setItem('modalsDisplayTimestamp', JSON.stringify({ ...modals, [this.name]: Date.now() }))

      this.$refs.modal.focus()
      this.$emit('opened', evt)
    },
    modalClosed (evt) {
      this.$emit('closed', evt)
      window.removeEventListener('resize', this.setModalFullscreen)
    },
    beforeModalOpen (evt) {
      document.querySelector('body').classList.add('simple-overflow-hidden')
      this.$emit('before-open', evt)
      if (this.fullscreenBreakpoint) {
        this.setModalFullscreen()
        window.addEventListener('resize', this.setModalFullscreen)
      }
    },
    beforeModalClose (evt) {
      document.querySelector('body').classList.remove('simple-overflow-hidden')
      this.$emit('before-close', evt)
    },
    emitScroll () {
      this.$emit('scroll')
    }
  }
}
</script>

<style lang="sass">
.b-modal
  .v--modal-overlay
    height: 100%
  .v--modal-background-click
    display: flex
    justify-content: center
    align-items: center
    height: 100%!important
  .v--modal
    display: flex
    flex-direction: column
    max-height: 100%
  .modal
    max-height: unset

.v--modal-overlay
  background: rgba(0, 0, 0, 0.5)
  padding: 10px 0
  z-index: 1001

.has-detached-content
  overflow: auto
  .v--modal
    background-color: transparent
    box-shadow: none
    max-height: unset
    .modal
      background-color: #fff
      border-radius: 3px
      max-height: unset
      &--detached
        margin-top: 10px
        .modal-body
          padding-top: 20px
          padding-bottom: 20px
  .v--modal-background-click
    height: auto!important

.modal
  overflow-y: auto
  overflow-x: hidden
  position: relative
  max-height: calc(100vh - 60px)
  display: flex
  flex-direction: column
  &:focus
    outline: none
  &-body
    padding: 0 20px 20px
    height: 100%
    position: relative
    +mmin(450)
      padding: 10px 30px 40px
    &:empty
      display: none
  &-heading
    padding: 20px 45px 20px 20px
    +mmin(450)
      padding: 25px 55px 10px 30px

.v--modal
  max-width: calc(100vw - 30px)
  margin: 0 auto
  .title-center .modal__title
    text-align: center

.modal__title
  font-weight: 500 //Rubik 300
  font-size: 24px
  line-height: 1.25
  color: $text-color
  margin: 0
  & + .modal__subtitle
    margin: 0
    margin-top: 18px
  &--sm
    font-size: 18px

  &--center
    text-align: center
  +mmax(450)
    font-size: 18px
  +mmaxh(450)
    font-size: 18px

.modal__subtitle
  color: $placeholder-color
  font-size: 16px
  display: block

.modal__close
  cursor: pointer
  position: absolute
  display: block
  width: 35px
  height: 35px
  top: 10px
  right: 10px
  border: 0
  padding: 0
  background-color: transparent
  border-radius: 3px
  transition: background-color .2s ease
  z-index: 1001

  +mmin(450)
    top: 20px
    right: 20px

  &:hover
    opacity: 1
    background-color: #F8F8F8

  &:before,
  &:after
    content: ''
    position: absolute
    top: calc(50% - 10px)
    left: calc(50% - 1px)
    height: 20px
    width: 2px
    background-color: #909090

  &:before
    transform: rotate(45deg)

  &:after
    transform: rotate(-45deg)

=fullscreen-mixin
  position: fixed!important
  left: 0!important
  top: 0!important
  bottom: 0!important
  right: 0!important
  max-width: 100vw!important
  width: 100vw!important
  border-radius: 0px
  margin: 0

.fullscreen
  .v--modal
    +fullscreen-mixin
    .modal
      height: 100%
      max-height: 100%

</style>
